import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useAccess } from '@/contexts/AccessContext';
import { startProUpgrade } from '@/utils/upgrade';
import AppShell from './AppShell';
import SubscriptionRequired from './SubscriptionRequired';
import { useDashboardWidgets, WidgetConfigurator } from './DashboardWidgets';
import { translatePhase } from '../utils/fieldTranslations';
import { Settings } from 'lucide-react';
import { onProgressRefresh } from '@/utils/progressEvents';
import axios from 'axios';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

function SimpleDashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, organization } = useAuth();
  const { isFree, canCreate, canUseIso, canUseMiljofyrtarn, tier, loading: accessLoading } = useAccess();
  
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [mode, setMode] = useState('internkontroll');
  const [progress, setProgress] = useState(null);
  const [showDeviationModal, setShowDeviationModal] = useState(false);
  const [isoStatus, setIsoStatus] = useState(null);
  const [mfStatus, setMfStatus] = useState(null);
  const [activatingIso, setActivatingIso] = useState(false);
  const [activatingMf, setActivatingMf] = useState(false);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [permissions, setPermissions] = useState(null);
  const [showWidgetConfig, setShowWidgetConfig] = useState(false);
  const [gapSessions, setGapSessions] = useState([]);
  const [levels, setLevels] = useState(null);
  const [levelRecommendation, setLevelRecommendation] = useState(null);
  const [pctPulse, setPctPulse] = useState(false);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [showWeeklyBanner, setShowWeeklyBanner] = useState(false);
  const { widgets, saveWidgets, isWidgetEnabled, loaded: widgetsLoaded } = useDashboardWidgets();

  useEffect(() => {
    fetchDashboard();
    fetchIsoStatus();
    fetchMfStatus();
    fetchPermissions();
    fetchGapSessions();
    fetchLevels();
    fetchWeeklySummary();
  }, []);

  // Listen for progress-refresh events from other pages (task/subtask/deviation status changes)
  useEffect(() => {
    const unsub = onProgressRefresh(() => {
      fetchLevels();
      fetchDashboard();
      fetchProgress();
    });
    return unsub;
  }, [mode]);

  useEffect(() => {
    fetchProgress();
  }, [mode]);

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/users/me/permissions`, { headers: { Authorization: `Bearer ${token}` } });
      setPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  };

  const fetchGapSessions = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/gap-analysis/sessions`, { headers: { Authorization: `Bearer ${token}` } });
      setGapSessions(response.data.sessions || []);
    } catch (error) {
      // Pro-gated: silently ignore 403 / non-Pro users
    }
  };

  const fetchLevels = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const [levelsRes, tasksRes, deviationsRes] = await Promise.all([
        axios.get(`${API}/ops/progress/levels`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/ops/tasks`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { tasks: [] } })),
        axios.get(`${API}/ops/deviations?status=open`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { deviations: [] } })),
      ]);

      // Compare with previous overall_pct to compute delta (for micro-feedback).
      // Uses localStorage so delta is detected across page navigations (e.g. user
      // completes a task on /tasks, then returns to /dashboard).
      setLevels((prev) => {
        const nextData = levelsRes.data;
        const storedPctRaw = localStorage.getItem('systemklar_overall_pct');
        const storedPct = storedPctRaw !== null ? Number(storedPctRaw) : null;
        const comparePct = typeof prev?.overall_pct === 'number' ? prev.overall_pct : storedPct;
        if (typeof comparePct === 'number' && nextData.overall_pct > comparePct) {
          const delta = nextData.overall_pct - comparePct;
          toast.success(
            t('dashboard.levels.deltaToast', {
              delta,
              defaultValue: `+${delta}% fremdrift – du er på vei`,
            }),
            { duration: 3500, icon: '📈' }
          );
          setPctPulse(true);
          setTimeout(() => setPctPulse(false), 1200);
        }
        try { localStorage.setItem('systemklar_overall_pct', String(nextData.overall_pct)); } catch (_) {}
        return nextData;
      });

      // Derive ONE concrete recommendation based on current state (non-technical, action-oriented)
      const openDevs = (deviationsRes.data.deviations || []).length;
      const allTasks = tasksRes.data.tasks || [];
      const prioWeight = { high: 3, medium: 2, low: 1 };
      const nextTask = allTasks
        .filter(tk => !tk.parent_task_id && tk.status !== 'completed' && !tk.locked)
        .sort((a, b) => {
          const sA = a.status === 'in_progress' ? 0 : 1;
          const sB = b.status === 'in_progress' ? 0 : 1;
          if (sA !== sB) return sA - sB;
          const pA = prioWeight[a.priority] || 0;
          const pB = prioWeight[b.priority] || 0;
          if (pA !== pB) return pB - pA;
          const dA = a.deadline ? new Date(a.deadline).getTime() : Infinity;
          const dB = b.deadline ? new Date(b.deadline).getTime() : Infinity;
          return dA - dB;
        })[0];
      setLevelRecommendation({ nextTask, openDeviations: openDevs });
    } catch (error) {
      console.error('Failed to fetch levels:', error);
    }
  };

  const fetchWeeklySummary = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/progress/weekly-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWeeklySummary(response.data);

      // Decide whether to show the banner:
      // - Show if never shown before, OR more than 7 days since last shown
      // - Don't spam: one dismissal per ISO week
      const lastShownRaw = localStorage.getItem('systemklar_weekly_banner_last_shown');
      const lastShown = lastShownRaw ? new Date(lastShownRaw).getTime() : 0;
      const nowMs = Date.now();
      const weekMs = 7 * 24 * 60 * 60 * 1000;
      if (nowMs - lastShown > weekMs) {
        setShowWeeklyBanner(true);
      }
    } catch (error) {
      // Silently ignore — banner is non-critical
    }
  };

  const dismissWeeklyBanner = () => {
    try {
      localStorage.setItem('systemklar_weekly_banner_last_shown', new Date().toISOString());
    } catch (_) { /* ignore */ }
    setShowWeeklyBanner(false);
  };


  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/progress/${mode}`, { headers: { Authorization: `Bearer ${token}` } });
      setProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  const fetchIsoStatus = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/iso-flow/status`, { headers: { Authorization: `Bearer ${token}` } });
      setIsoStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch ISO status:', error);
    }
  };

  const fetchMfStatus = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/miljofyrtarn/status`, { headers: { Authorization: `Bearer ${token}` } });
      setMfStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch MF status:', error);
    }
  };

  const activateIsoFlow = async () => {
    if (selectedStandards.length === 0) {
      toast.error(t('dashboard.errors.selectAtLeastOne'));
      return;
    }
    setActivatingIso(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/ops/iso-flow/activate`, { standards: selectedStandards }, { headers: { Authorization: `Bearer ${token}` } });
      const { tasks_created = 0, tasks_covered_by_ik = 0, tasks_stricter = 0 } = response.data || {};
      let msg = t('dashboard.isoActivated', { count: tasks_created });
      if (tasks_covered_by_ik > 0) msg += ' ' + t('dashboard.coveredByIk', { count: tasks_covered_by_ik });
      if (tasks_stricter > 0) msg += ' ' + t('dashboard.stricterThanIk', { count: tasks_stricter });
      toast.success(msg, { duration: 7000 });
      fetchIsoStatus();
      fetchDashboard();
      fetchProgress();
    } catch (error) {
      toast.error(t('dashboard.errors.couldNotActivateIso'));
    } finally {
      setActivatingIso(false);
    }
  };

  const activateMfFlow = async () => {
    setActivatingMf(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/ops/miljofyrtarn/activate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const { tasks_created = 0, tasks_covered_by_ik = 0, tasks_stricter = 0 } = response.data || {};
      let msg = t('dashboard.mfActivated', { count: tasks_created });
      if (tasks_covered_by_ik > 0) msg += ' ' + t('dashboard.coveredByIk', { count: tasks_covered_by_ik });
      if (tasks_stricter > 0) msg += ' ' + t('dashboard.stricterThanIk', { count: tasks_stricter });
      toast.success(msg, { duration: 7000 });
      fetchMfStatus();
      fetchDashboard();
      fetchProgress();
    } catch (error) {
      toast.error(t('dashboard.errors.couldNotActivateMf'));
    } finally {
      setActivatingMf(false);
    }
  };

  const toggleStandard = (id) => {
    setSelectedStandards(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const submitDeviation = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.post(`${API}/ops/deviations`, {
        description: formData.get('description'),
        severity: formData.get('severity'),
        area: formData.get('area')
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Sak registrert!');
      setShowDeviationModal(false);
      fetchDashboard();
    } catch (error) {
      toast.error(t('dashboard.errors.couldNotRegisterCase'));
    }
  };

  const getModeLabel = (m) => {
    const labels = { 
      'internkontroll': t('dashboard.modes.internkontroll'), 
      'iso': t('dashboard.modes.iso'), 
      'miljofyrtarn': t('dashboard.modes.miljofyrtarn') 
    };
    return labels[m] || m;
  };

  // Translate progress step names from backend
  const translateProgressStep = (step) => {
    if (!step) return '';
    const translations = {
      'IK-system generert': t('progress.ikSystemGenerated'),
      'Ingen åpne avvik': t('progress.noOpenDeviations'),
      'Oppgaver under kontroll': t('progress.tasksUnderControl'),
      'Risikovurdering gjennomført': t('progress.riskAssessmentDone'),
      'Årshjul aktivert': t('progress.yearWheelActivated'),
      'Avvikssystem aktivt': t('progress.deviationSystemActive'),
      'Risikovurdering dokumentert': t('progress.riskAssessmentDocumented'),
      'Mål og policy definert': t('progress.goalsAndPolicyDefined'),
      'Internrevisjon planlagt': t('progress.internalAuditPlanned'),
      'Ledelsens gjennomgang': t('progress.managementReview'),
      'Miljøkartlegging': t('progress.environmentalMapping'),
      'Avfallshåndtering dokumentert': t('progress.wasteHandlingDocumented'),
      'Energiforbruk kartlagt': t('progress.energyConsumptionMapped'),
      'Handlingsplan laget': t('progress.actionPlanCreated'),
    };
    return translations[step] || step;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'low': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'medium': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'high': 'bg-rose-500/20 text-rose-400 border-rose-500/30'
    };
    return colors[severity] || 'bg-slate-500/20 text-slate-400';
  };

  const getSeverityLabel = (severity) => {
    return t(`cases.severity.${severity}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <AppShell permissions={permissions}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                {t('dashboard.title')}
              </h1>
              <p className="text-slate-400">
                {t('dashboard.welcomeBack')}, {user?.name || user?.email?.split('@')[0]}
              </p>
            </div>
            <button
              onClick={() => setShowWidgetConfig(true)}
              className="p-2.5 bg-slate-800/60 border border-slate-700/50 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
              title="Tilpass dashboard"
              data-testid="dashboard-customize-btn"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Weekly progress summary banner (retention) */}
        {showWeeklyBanner && weeklySummary && (
          <div
            className={`mb-6 rounded-2xl border p-4 sm:p-5 flex items-start gap-4 ${
              weeklySummary.has_activity
                ? 'border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-slate-900/40 to-emerald-500/5'
                : 'border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-slate-900/40 to-amber-500/5'
            }`}
            data-testid="weekly-summary-banner"
          >
            <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
              weeklySummary.has_activity ? 'bg-emerald-500/20' : 'bg-amber-500/20'
            }`}>
              <svg className={`w-5 h-5 ${weeklySummary.has_activity ? 'text-emerald-300' : 'text-amber-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {weeklySummary.has_activity ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                )}
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-1">
                {t('dashboard.weekly.label', { defaultValue: 'Forrige uke' })}
              </p>
              <p className="text-base sm:text-lg font-semibold text-white leading-snug" data-testid="weekly-summary-text">
                {weeklySummary.has_activity
                  ? t('dashboard.weekly.summary', {
                      delta: weeklySummary.delta_pct,
                      tasks: weeklySummary.tasks_completed,
                      devs: weeklySummary.deviations_closed,
                      defaultValue: `+${weeklySummary.delta_pct}% fremdrift · ${weeklySummary.tasks_completed} oppgaver fullført · ${weeklySummary.deviations_closed} avvik lukket`,
                    })
                  : t('dashboard.weekly.noActivity', { defaultValue: 'Ingen fremdrift forrige uke – la oss komme i gang igjen' })
                }
              </p>
              <div className="flex items-center gap-3 mt-3">
                <button
                  onClick={() => {
                    dismissWeeklyBanner();
                    if (levelRecommendation?.nextTask) {
                      navigate(`/tasks?highlight=${levelRecommendation.nextTask.id}`);
                    } else {
                      navigate('/tasks');
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors shadow-lg ${
                    weeklySummary.has_activity
                      ? 'bg-emerald-500 text-slate-950 hover:bg-emerald-400 shadow-emerald-500/25'
                      : 'bg-amber-500 text-slate-950 hover:bg-amber-400 shadow-amber-500/25'
                  }`}
                  data-testid="weekly-summary-cta"
                >
                  {t('dashboard.weekly.continueCta', { defaultValue: 'Fortsett der du slapp →' })}
                </button>
                <button
                  onClick={dismissWeeklyBanner}
                  className="text-xs text-slate-400 hover:text-white transition-colors"
                  data-testid="weekly-summary-dismiss"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 3-level platform progress widget (Fra krav → Kontroll → Sertifisering) */}
        {levels && (levels.overall_total > 0) && (() => {
          const lvls = [levels.level_1, levels.level_2, levels.level_3];
          // Current active level = first non-100% level (or level 3 if all done)
          const activeIdx = lvls.findIndex(l => l.pct < 100);
          const currentLevel = activeIdx === -1 ? 2 : activeIdx;
          const currentLabel = (i18n.language || 'nb').startsWith('en')
            ? lvls[currentLevel].label_en
            : lvls[currentLevel].label_nb;
          const colors = [
            { text: 'text-amber-300', bg: 'from-amber-400 to-amber-500', dot: 'bg-amber-400', border: 'border-amber-500/30' },
            { text: 'text-emerald-300', bg: 'from-emerald-400 to-emerald-500', dot: 'bg-emerald-400', border: 'border-emerald-500/30' },
            { text: 'text-cyan-300', bg: 'from-cyan-400 to-blue-500', dot: 'bg-cyan-400', border: 'border-cyan-500/30' },
          ];
          const rec = levelRecommendation || {};
          // Build ONE concrete recommendation (non-technical)
          let recText = null;
          if (rec.openDeviations > 0 && rec.nextTask) {
            recText = t('dashboard.levels.recWithBoth', {
              task: rec.nextTask.title,
              count: rec.openDeviations,
              defaultValue: `Fullfør «${rec.nextTask.title}» og lukk ${rec.openDeviations} åpne avvik`
            });
          } else if (rec.nextTask) {
            recText = t('dashboard.levels.recTaskOnly', {
              task: rec.nextTask.title,
              defaultValue: `Neste anbefalte oppgave: ${rec.nextTask.title}`
            });
          } else if (rec.openDeviations > 0) {
            recText = t('dashboard.levels.recDevsOnly', {
              count: rec.openDeviations,
              defaultValue: `Lukk ${rec.openDeviations} åpne avvik`
            });
          } else if (activeIdx === -1) {
            recText = t('dashboard.levels.recDone', {
              defaultValue: 'Flott jobba! Alle nivåer er i mål – hold systemet levende uke for uke.'
            });
          }
          return (
            <div
              className={`mb-6 rounded-2xl border ${colors[currentLevel].border} bg-gradient-to-br from-slate-900/80 via-slate-900/40 to-slate-900/60 backdrop-blur-sm p-5 sm:p-6`}
              data-testid="levels-widget"
            >
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div className="min-w-0">
                  <p className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-1">
                    {t('dashboard.levels.label', { defaultValue: 'Din reise' })}
                  </p>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    {t('dashboard.levels.youAreOn', { defaultValue: 'Du er på' })}{' '}
                    <span className={colors[currentLevel].text}>
                      {t('dashboard.levels.levelPrefix', { defaultValue: 'Nivå' })} {currentLevel + 1} · {currentLabel}
                    </span>
                  </h2>
                </div>
                <div className="text-right">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                    {t('dashboard.levels.overall', { defaultValue: 'Samlet fremdrift' })}
                  </p>
                  <p
                    className={`text-3xl sm:text-4xl font-bold tabular-nums transition-all duration-500 ${colors[currentLevel].text} ${pctPulse ? 'scale-110 drop-shadow-[0_0_12px_rgba(34,211,238,0.6)]' : 'scale-100'}`}
                    data-testid="levels-overall-pct"
                  >
                    {levels.overall_pct}%
                  </p>
                </div>
              </div>

              {/* 3 parallel level bars */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {lvls.map((lvl, idx) => {
                  const label = (i18n.language || 'nb').startsWith('en') ? lvl.label_en : lvl.label_nb;
                  const isActive = idx === currentLevel;
                  const isDone = lvl.pct >= 100;
                  return (
                    <div
                      key={lvl.key}
                      className={`rounded-xl p-3 transition-all ${
                        isActive
                          ? `border ${colors[idx].border} bg-slate-950/60`
                          : isDone
                            ? 'border border-emerald-500/20 bg-emerald-500/5'
                            : 'border border-white/5 bg-slate-950/30 opacity-80'
                      }`}
                      data-testid={`level-${idx + 1}-card`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex w-5 h-5 rounded-full items-center justify-center text-[10px] font-bold text-slate-950 ${colors[idx].dot}`}>
                          {idx + 1}
                        </span>
                        <p className={`text-sm font-semibold truncate ${isActive ? 'text-white' : 'text-slate-300'}`}>
                          {label}
                        </p>
                        {isDone && (
                          <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="flex items-baseline justify-between mb-1.5">
                        <span className={`text-xs font-mono tabular-nums ${isActive ? colors[idx].text : 'text-slate-500'}`}>
                          {lvl.done}/{lvl.total}
                        </span>
                        <span className={`text-xs font-bold tabular-nums ${isActive ? colors[idx].text : 'text-slate-500'}`}>
                          {lvl.pct}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800/80">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${colors[idx].bg} transition-all duration-500 ease-out`}
                          style={{ width: `${lvl.pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Concrete recommendation */}
              {recText && (
                <button
                  onClick={() => {
                    if (rec.nextTask) navigate(`/tasks?highlight=${rec.nextTask.id}`);
                    else navigate('/deviations');
                  }}
                  className={`w-full text-left flex items-center gap-3 rounded-xl border ${colors[currentLevel].border} bg-slate-950/40 hover:bg-slate-950/70 transition-colors p-3 group`}
                  data-testid="levels-recommendation"
                >
                  <span className={`inline-flex w-8 h-8 rounded-lg ${colors[currentLevel].dot}/20 items-center justify-center flex-shrink-0`}>
                    <svg className={`w-4 h-4 ${colors[currentLevel].text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
                      {t('dashboard.levels.nextStep', { defaultValue: 'Neste steg' })}
                    </p>
                    <p className="text-sm font-medium text-white line-clamp-2 group-hover:text-cyan-100">
                      {recText}
                    </p>
                  </div>
                  <svg className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
          );
        })()}

        {/* Mode Selector */}
        <div className="mb-6 flex flex-wrap gap-2">
          {['internkontroll', 'iso', 'miljofyrtarn'].map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                mode === m 
                  ? 'bg-cyan-500 text-slate-950 shadow-lg shadow-cyan-500/25' 
                  : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-700/50'
              }`}
            >
              {getModeLabel(m)}
            </button>
          ))}
        </div>

        {/* Progress Card */}
        {isWidgetEnabled('progress') && progress && (
          <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-slate-300">
                {getModeLabel(mode)}{t('dashboard.statusSuffix')}
              </span>
              <span className="text-3xl font-bold text-cyan-400">
                {progress.progress_percent}%
              </span>
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2.5 mb-3">
              <div 
                className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2.5 rounded-full transition-all shadow-sm shadow-cyan-500/50"
                style={{ width: `${progress.progress_percent}%` }}
              ></div>
            </div>
            {progress.next_step && (
              <p className="text-sm text-slate-400">
                {t('dashboard.nextStep')}: <span className="text-slate-200">{translateProgressStep(progress.next_step)}</span>
              </p>
            )}
          </div>
        )}

        {/* ISO Activation Card */}
        {isWidgetEnabled('iso_activation') && mode === 'iso' && isoStatus && !isoStatus.activated && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-6 mb-6" data-testid="iso-activate-card">
            {!canUseIso ? (
              <>
                <p className="font-semibold text-white mb-1">{t('dashboard.iso.selectStandards')}</p>
                <SubscriptionRequired 
                  hasAccess={false}
                  featureName="ISO-moduler"
                  showCompact={true}
                  requiresPro={true}
                  currentTier={tier}
                />
              </>
            ) : (
              <>
                <p className="font-semibold text-white mb-1">{t('dashboard.iso.selectStandards')}</p>
                <p className="text-sm text-slate-400 mb-4">
                  {t('dashboard.iso.generatesTasksDescription')}
                </p>
                <div className="flex flex-wrap gap-3 mb-4">
                  {[
                    { id: '9001', label: `ISO 9001 – ${t('dashboard.iso.quality')}` },
                    { id: '14001', label: `ISO 14001 – ${t('dashboard.iso.environment')}` },
                    { id: '45001', label: `ISO 45001 – ${t('dashboard.iso.hms')}` },
                  ].map(std => (
                    <button
                      key={std.id}
                      type="button"
                      onClick={() => toggleStandard(std.id)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        selectedStandards.includes(std.id)
                          ? 'bg-cyan-500 text-slate-950 shadow-lg shadow-cyan-500/25'
                          : 'bg-slate-800/50 text-slate-300 border border-slate-700 hover:border-cyan-500/50'
                      }`}
                      data-testid={`standard-toggle-${std.id}`}
                    >
                      {std.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={activateIsoFlow}
                  disabled={activatingIso || selectedStandards.length === 0}
                  className="px-6 py-3 bg-cyan-500 text-slate-950 rounded-xl text-sm font-bold hover:bg-cyan-400 disabled:opacity-40 transition-all shadow-lg shadow-cyan-500/25"
                  data-testid="activate-iso-btn"
                >
                  {activatingIso ? t('dashboard.iso.activating') : `${t('dashboard.iso.startJourney')}${selectedStandards.length > 0 ? ` (${selectedStandards.length} ${t('dashboard.iso.selected')})` : ''}`}
                </button>
              </>
            )}
          </div>
        )}

        {/* ISO Progress Card */}
        {mode === 'iso' && isoStatus && isoStatus.activated && (
          <div className={`rounded-2xl border p-6 mb-6 ${isoStatus.needs_upgrade ? 'bg-amber-500/10 border-amber-500/20' : 'bg-emerald-500/10 border-emerald-500/20'}`} data-testid="iso-progress-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-white">
                  {isoStatus.needs_upgrade ? t('dashboard.iso.wantToComplete') : t('dashboard.iso.active')}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  {isoStatus.standards && isoStatus.standards.length > 0 && (
                    <span className="text-slate-500">
                      {isoStatus.standards.map(s => ({'9001': t('dashboard.iso.quality'),'14001': t('dashboard.iso.environment'),'45001': t('dashboard.iso.hms')}[s])).join(' + ')} — {' '}
                    </span>
                  )}
                  {isoStatus.completed} {t('common.of')} {isoStatus.total} {t('dashboard.iso.tasksCompleted')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-2xl font-bold ${isoStatus.needs_upgrade ? 'text-amber-400' : 'text-emerald-400'}`}>{isoStatus.percent}%</span>
                {isoStatus.needs_upgrade ? (
                  <button className="px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 transition-all" data-testid="dashboard-upgrade-btn" onClick={startProUpgrade}>
                    {t('dashboard.upgrade')}
                  </button>
                ) : (
                  <Link to="/tasks" className="px-4 py-2 border border-emerald-500/50 text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-500/10 transition-all" data-testid="iso-view-tasks-btn">
                    {t('dashboard.seeTasks')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Miljøfyrtårn Activation Card */}
        {isWidgetEnabled('mf_activation') && mode === 'miljofyrtarn' && mfStatus && !mfStatus.activated && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6 mb-6" data-testid="mf-activate-card">
            {!canUseMiljofyrtarn ? (
              <>
                <p className="font-semibold text-white mb-1">{t('dashboard.mf.readyTitle')}</p>
                <SubscriptionRequired 
                  hasAccess={false}
                  featureName="Miljøfyrtårn"
                  showCompact={true}
                  requiresPro={true}
                  currentTier={tier}
                />
              </>
            ) : (
              <>
                <p className="font-semibold text-white mb-1">{t('dashboard.mf.readyTitle')}</p>
                <p className="text-sm text-slate-400 mb-4">
                  {t('dashboard.mf.description')}
                </p>
                <button
                  onClick={activateMfFlow}
                  disabled={activatingMf}
                  className="px-6 py-3 bg-emerald-500 text-slate-950 rounded-xl text-sm font-bold hover:bg-emerald-400 disabled:opacity-40 transition-all shadow-lg shadow-emerald-500/25"
                  data-testid="activate-mf-btn"
                >
                  {activatingMf ? t('dashboard.iso.activating') : t('dashboard.mf.startJourney')}
                </button>
              </>
            )}
          </div>
        )}

        {/* Miljøfyrtårn Progress Card */}
        {mode === 'miljofyrtarn' && mfStatus && mfStatus.activated && (
          <div className={`rounded-2xl border p-6 mb-6 ${mfStatus.needs_upgrade ? 'bg-amber-500/10 border-amber-500/20' : 'bg-emerald-500/10 border-emerald-500/20'}`} data-testid="mf-progress-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-white">
                  {mfStatus.needs_upgrade ? t('dashboard.mf.wantToComplete') : t('dashboard.mf.active')}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  {mfStatus.completed} {t('common.of')} {mfStatus.total} {t('dashboard.iso.tasksCompleted')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-2xl font-bold ${mfStatus.needs_upgrade ? 'text-amber-400' : 'text-emerald-400'}`}>{mfStatus.percent}%</span>
                {mfStatus.needs_upgrade ? (
                  <button className="px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 transition-all" data-testid="mf-upgrade-btn" onClick={startProUpgrade}>
                    {t('dashboard.upgrade')}
                  </button>
                ) : (
                  <Link to="/tasks" className="px-4 py-2 border border-emerald-500/50 text-emerald-400 rounded-lg text-sm font-medium hover:bg-emerald-500/10 transition-all" data-testid="mf-view-tasks-btn">
                    {t('dashboard.seeTasks')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Subscription Banner for Free Users */}
        {isFree && !accessLoading && (
          <div className="mb-8">
            <SubscriptionRequired 
              hasAccess={false} 
              featureName={t('common.features', 'funksjoner')}
              showCompact={true}
            />
          </div>
        )}

        {/* Quick Actions */}
        {isWidgetEnabled('quick_actions') && (
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <button
            onClick={() => canCreate ? setShowDeviationModal(true) : navigate('/subscription')}
            className={`flex-1 ${canCreate ? 'bg-rose-500 hover:bg-rose-400' : 'bg-slate-700 cursor-not-allowed'} text-white py-4 px-6 rounded-xl font-semibold transition-all shadow-lg ${canCreate ? 'shadow-rose-500/25' : ''} flex items-center justify-center gap-2`}
            data-testid="register-deviation-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {canCreate ? t('dashboard.registerCase') : t('common.upgradeToCreate', 'Oppgrader for å registrere')}
          </button>
          <Link
            to={canCreate ? "/generate" : "/subscription"}
            className={`flex-1 ${canCreate ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-950' : 'bg-slate-700 text-white cursor-not-allowed'} py-4 px-6 rounded-xl font-semibold transition-all shadow-lg ${canCreate ? 'shadow-cyan-500/25' : ''} flex items-center justify-center gap-2`}
            data-testid="generate-ik-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            {canCreate ? t('dashboard.generateIK') : t('common.upgradeToGenerate', 'Oppgrader for å generere')}
          </Link>
        </div>
        )}

        {/* Summary Cards */}
        {isWidgetEnabled('summary') && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-6 text-center">
            <div className="text-4xl font-bold text-rose-400 mb-1">
              {dashboard?.summary?.open_deviations || 0}
            </div>
            <div className="text-sm text-slate-400">{t('dashboard.openCases')}</div>
          </div>
          <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-6 text-center">
            <div className="text-4xl font-bold text-amber-400 mb-1">
              {dashboard?.summary?.overdue_tasks || 0}
            </div>
            <div className="text-sm text-slate-400">{t('dashboard.overdue')}</div>
          </div>
          <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-6 text-center">
            <div className="text-4xl font-bold text-cyan-400 mb-1">
              {dashboard?.summary?.tasks_this_week || 0}
            </div>
            <div className="text-sm text-slate-400">{t('dashboard.thisWeek')}</div>
          </div>
        </div>
        )}

        {/* Open Cases Section */}
        {isWidgetEnabled('open_cases') && dashboard?.open_deviations?.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">{t('dashboard.openCases')}</h2>
              <Link to="/deviations" className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors" data-testid="see-all-deviations">
                {t('dashboard.seeAll')} →
              </Link>
            </div>
            <div className="space-y-3">
              {dashboard.open_deviations.slice(0, 5).map(dev => (
                <div key={dev.id} className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-xl p-4 flex items-center justify-between hover:border-slate-700 transition-all">
                  <div className="flex-1">
                    <p className="text-slate-200 font-medium">{dev.title || dev.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-xs px-2.5 py-1 rounded-full border ${getSeverityColor(dev.severity)}`}>
                        {getSeverityLabel(dev.severity)}
                      </span>
                      {dev.deadline && (
                        <span className="text-xs text-slate-500">
                          {t('common.deadline')}: {formatDate(dev.deadline)}
                        </span>
                      )}
                    </div>
                  </div>
                  <Link 
                    to={`/deviations?open=${dev.id}`}
                    className="px-4 py-2 text-sm text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                  >
                    {t('common.open')}
                  </Link>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Upcoming Tasks Section */}
        {isWidgetEnabled('upcoming_tasks') && dashboard?.upcoming_tasks?.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">{t('dashboard.upcomingTasks')}</h2>
              <Link to="/tasks" className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors" data-testid="see-all-tasks">
                {t('dashboard.seeAll')} →
              </Link>
            </div>
            <div className="space-y-3">
              {dashboard.upcoming_tasks.slice(0, 5).map(task => (
                <div key={task.id} className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-xl p-4 flex items-center justify-between hover:border-slate-700 transition-all">
                  <div className="flex-1">
                    <p className="text-slate-200 font-medium">{task.title}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {task.due_date && (
                        <span className={`text-xs px-2.5 py-1 rounded-full ${
                          new Date(task.due_date) < new Date() 
                            ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' 
                            : 'bg-slate-700/50 text-slate-400'
                        }`}>
                          {formatDate(task.due_date)}
                        </span>
                      )}
                      {task.phase && (
                        <span className="text-xs text-slate-500">{translatePhase(task.phase, i18n.language)}</span>
                      )}
                    </div>
                  </div>
                  <Link 
                    to={`/tasks?open=${task.id}`}
                    className="px-4 py-2 text-sm text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                  >
                    Åpne
                  </Link>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* GAP Analysis Widget */}
        {isWidgetEnabled('gap_analysis') && gapSessions.length > 0 && (
          <section className="mb-8" data-testid="gap-analysis-widget">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 3l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                {t('dashboard.gapAnalysisTitle')}
              </h2>
              <Link to="/gap-analysis" className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors" data-testid="see-all-gap">
                {t('dashboard.seeAll')} →
              </Link>
            </div>
            <div className="space-y-3">
              {gapSessions.slice(0, 3).map(s => {
                const total = (s.met_count || 0) + (s.partial_count || 0) + (s.missing_count || 0);
                const metPct = total > 0 ? Math.round(((s.met_count || 0) / total) * 100) : 0;
                return (
                  <Link
                    key={s.id}
                    to="/gap-analysis"
                    className="block bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-xl p-4 hover:border-slate-700 transition-all"
                    data-testid={`gap-widget-session-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-200 font-medium truncate">{s.title}</p>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          {(s.standards || []).map(sid => (
                            <span key={sid} className="text-xs px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400">
                              {sid.replace('iso_', 'ISO ').replace('_', ':')}
                            </span>
                          ))}
                        </div>
                        {total > 0 ? (
                          <div className="flex gap-3 mt-2 text-xs items-center">
                            <span className="text-emerald-400">✓ {s.met_count || 0}</span>
                            <span className="text-amber-400">◐ {s.partial_count || 0}</span>
                            <span className="text-rose-400">✗ {s.missing_count || 0}</span>
                            <span className="text-slate-500">• {total} {t('dashboard.gapClauses')}</span>
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500 mt-2">{t('dashboard.gapNoAnalysisYet')}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {total > 0 && (
                          <div className="text-right">
                            <div className="text-2xl font-bold text-white leading-none">{metPct}%</div>
                            <div className="text-xs text-slate-500">{t('dashboard.gapCompliance')}</div>
                          </div>
                        )}
                        <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* Deviation Modal */}
      {showDeviationModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">{t('cases.registerNew')}</h2>
              <button onClick={() => setShowDeviationModal(false)} className="text-slate-500 hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={submitDeviation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t('cases.description')}</label>
                <textarea
                  name="description"
                  rows="3"
                  required
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                  placeholder={t('cases.descriptionPlaceholder')}
                ></textarea>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t('cases.severity.label')}</label>
                  <select name="severity" className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all">
                    <option value="low">{t('cases.severity.low')}</option>
                    <option value="medium">{t('cases.severity.medium')}</option>
                    <option value="high">{t('cases.severity.high')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t('cases.area')}</label>
                  <select name="area" className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all">
                    <option value="hms">HMS</option>
                    <option value="kvalitet">Kvalitet</option>
                    <option value="miljo">Miljø</option>
                    <option value="annet">Annet</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowDeviationModal(false)}
                  className="flex-1 px-4 py-3 bg-slate-800 text-slate-300 rounded-xl font-medium hover:bg-slate-700 transition-all"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-cyan-500 text-slate-950 rounded-xl font-semibold hover:bg-cyan-400 transition-all shadow-lg shadow-cyan-500/25"
                >
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Widget Configurator Modal */}
      {showWidgetConfig && (
        <WidgetConfigurator
          widgets={widgets}
          onSave={saveWidgets}
          onClose={() => setShowWidgetConfig(false)}
        />
      )}
    </AppShell>
  );
}

export default SimpleDashboard;
